<template>
    <Head>
        <title>{{ title }}</title>
    </Head>
    <div class="app">
        <Search />
        <Sidebar
            v-if="!hideUI"
            :shown="shown"
            :minimized="minimized"
        />
        <DWrapper
            class="d-main-wrapper"
            :class="nonProductionEnvironmentBg"
        >
            <Header
                v-if="!hideUI && breadcrumbs.length > 0"
                :breadcrumbs="breadcrumbs"
            />

            <main class="d-main">
                <CronJobMonitoringAlert
                    v-if="$page.props.cronJobErrors.length > 0"
                    :cron-job-errors="$page.props.cronJobErrors"
                />
                <slot />
            </main>

            <DToaster
                v-if="$page.props.flash.message"
                placement="top-end"
                visible
            >
                <DToast
                    :key="'flash'"
                    color="success"
                    :visible="true"
                    :header-text="__('messages.success')"
                    :body-text="$page.props.flash.message"
                />
            </DToaster>
            <DToaster
                v-if="$page.props.flash.errorMessage"
                placement="top-end"
                visible
            >
                <DToast
                    :key="'flash'"
                    color="danger"
                    :visible="true"
                    :header-text="__('messages.error')"
                    :body-text="$page.props.flash.errorMessage"
                />
            </DToaster>
            <DToaster
                v-if="$page.props.flash.warningMessage"
                placement="top-end"
                visible
            >
                <DToast
                    :key="'flash'"
                    color="warning"
                    :autohide="false"
                    :visible="true"
                    :header-text="__('messages.warning')"
                    :body-text="$page.props.flash.warningMessage"
                />
            </DToaster>
            <DToaster
                v-if="$page.props.flash.errorMessages"
                placement="top-end"
                visible
            >
                <DToast
                    :key="'flash'"
                    color="danger"
                    :visible="true"
                    :header-text="__('messages.error')"
                    :body-text="$page.props.flash.errorMessages"
                />
            </DToaster>
            <DToaster
                v-if="showErrorToast"
                class="toaster"
                placement="bottom-end"
                visible
            >
                <DToast
                    :key="'flash'"
                    color="danger"
                    :visible="true"
                    :autohide="false"
                    :header-text="__('messages.errors.validation_failed')"
                    :body-text="errorMessage"
                    :body-class="true"
                    @close="closeToaster"
                />
            </DToaster>
            <DToaster
                v-if="showUnAuthenticatedToaster"
                class="toaster"
                placement="top-end"
                visible
            >
                <DToast
                    :key="'flash'"
                    color="danger"
                    :visible="true"
                    :autohide="false"
                    :header-text="__('messages.unauthenticated.title')"
                    :body-text="__('messages.unauthenticated.message')"
                    :body-class="true"
                    @close="showUnAuthenticatedToaster = false"
                />
            </DToaster>
            <DModal
                :show="showModal"
                :is-error="modalType === 'error'"
                :is-success="modalType === 'success'"
                :is-warning="modalType === 'warning'"
                :is-info="modalType === 'info'"
                :title="modalTitle"
                :message="modalBody"
                :error="modalError"
                :link-message="modalLinkMessage"
                :link="modalLink"
                is-big
                @close="showModal = false"
            />
        </DWrapper>
    </div>
</template>

<script>
import { router } from '@inertiajs/vue3'
import Sidebar from './Components/App/Sidebar/Sidebar.vue'
import Header from './Components/App/Header.vue'
import Search from './Components/Search/Search'
import sitemap from './Configurations/sitemap/sitemap'
import { getBreadcrumbs } from './Configurations/sitemap'
import CronJobMonitoringAlert from '~@/Components/Common/CronJobs/CronJobMonitoringAlert'
import { __ } from '~@/Composables/translation'
const timeoutTime = 5000

export default {
    name: 'App',
    components: {
        CronJobMonitoringAlert,
        Sidebar,
        Header,
        Search
    },
    emits: ['select-sidebar'],
    data () {
        return {
            shown: true,
            minimized: false,
            hideUI: true,
            breadcrumbs: [],
            title: 'Nitro',
            showErrorToast: false,
            errorMessage: '',
            showModal: false,
            modalType: null,
            modalTitle: 'Null',
            modalBody: null,
            modalError: null,
            modalLinkMessage: null,
            modalLink: null,
            showUnAuthenticatedToaster: false
        }
    },
    computed: {
        nonProductionEnvironmentBg () {
            return !this.$page.props.isProductionEnvironment ? 'non-production' : ''
        },
        errorMessages () {
            const errors = []
            if (this.$page.props.flash.errorMessages) {
                Object.keys(this.$page.props.flash.errorMessages).forEach((field) => {
                    this.$page.props.flash.errorMessages[field].forEach((message) => {
                        errors.push(message)
                    })
                })
            }
            return errors
        },
        sidebarTranslationParameters () {
            return this.$page.props.sidebarTranslationParameters
        }
    },
    watch: {
        '$page.props.flash.message': {
            handler () {
                if (this.$page.props.flash.message) {
                    setTimeout(function (scope) {
                        scope.$page.props.flash.message = null
                    }, timeoutTime, this)
                } else {
                    this.$page.props.flash.message = null
                }
            }
        },
        '$page.props.flash.warningMessage': {
            handler () {
                if (!this.$page.props.flash.warningMessage) {
                    this.$page.props.flash.warningMessage = null
                }
            }
        },
        '$page.props.flash.errorMessage': {
            handler () {
                if (this.$page.props.flash.errorMessage) {
                    setTimeout(function (scope) {
                        scope.$page.props.flash.errorMessage = null
                    }, timeoutTime, this)
                } else {
                    this.$page.props.flash.errorMessage = null
                }
            }
        },
        '$page.props.flash.errorMessages': {
            handler () {
                if (this.$page.props.flash.errorMessages) {
                    setTimeout(function (scope) {
                        scope.$page.props.flash.errorMessages = null
                    }, timeoutTime, this)
                } else {
                    this.$page.props.flash.errorMessages = null
                }
            }
        },
        '$page.props.errors': {
            handler () {
                if (Object.values(this.$page.props.errors).length > 0) {
                    this.showErrorToast = true
                    const numberOfErrors = Object.values(this.$page.props.errors).length
                    this.errorMessage = Object.values(this.$page.props.errors).length > 1
                        ? __('messages.errors.multiple_validation_errors', { numberOfErrors })
                        : __('messages.errors.single_validation_error', { numberOfErrors })
                } else {
                    this.closeToaster()
                }
            }
        },
        '$page.props.messages': {
            handler () {
                if (this.$page.props.messages) {
                    this.showModal = true
                    this.modalType = this.$page.props.messages.type
                    this.modalTitle = this.$page.props.messages.title
                    this.modalBody = this.$page.props.messages?.body ?? null
                    this.modalError = this.$page.props.messages?.error ?? null
                    this.modalLinkMessage = this.$page.props.messages?.linkMessage ?? null
                    this.modalLink = this.$page.props.messages?.link ?? null
                }
            }
        }
    },
    mounted () {
        // Apply saved theme preference
        const savedTheme = localStorage.getItem('theme')
        if (savedTheme === 'dark') {
            document.body.classList.add('dark-mode')
        }

        // Listen for theme changes
        this.emitter.on('theme-changed', (isDark) => {
            document.body.classList.toggle('dark-mode', isDark)
        })

        // If browser buttons are clicked remove flash messages
        window.onpopstate = () => {
            this.$page.props.flash.message = null
            this.$page.props.flash.errorMessage = null
            this.$page.props.flash.errorMessages = null
        }

        this.emitter.on('sidebar-toggle', () => {
            this.minimized = !this.minimized
        })

        this.updateBreadcrumbs()
        router.on('navigate', (event) => {
            this.hideUI = ['login.index', 'tenant.select'].includes(this.$route().current())
            this.updateBreadcrumbs()
        })
        window.axios.interceptors.response.use(
            response => response,
            error => {
                if (error.response.status === 401 || error.response.status === 403) {
                    this.showUnAuthenticatedToaster = true
                }
                return Promise.reject(error)
            }
        )
    },
    methods: {
        updateBreadcrumbs () {
            const fullBreadcrumbs = []

            const breadcrumbs = getBreadcrumbs(sitemap(this.$page), this.$route().current(), this.$route().params, this.$page.props)

            if (breadcrumbs !== null) {
                breadcrumbs.forEach(item => {
                    let currentRouteParameters = this.$route().params

                    // Check if object empty
                    if (Object.keys(item.href.params).length !== 0) {
                        currentRouteParameters = { ...currentRouteParameters, ...this.$page.props }
                        for (const currentParam in currentRouteParameters) {
                            // Delete unnecessary params
                            if (!item.href.params.includes(currentParam)) {
                                delete currentRouteParameters[currentParam]
                            }
                        }
                    } else {
                        currentRouteParameters = item.href.params
                    }

                    fullBreadcrumbs.push({
                        text: item.text,
                        href: item.href.route !== '' ? this.$route(item.href.route, currentRouteParameters) : ''
                    })
                }, this)
            }

            // Mark sidebar item as active
            this.emitter.emit('select-sidebar')

            this.breadcrumbs = fullBreadcrumbs
            const translationKey = fullBreadcrumbs.length > 0 ? fullBreadcrumbs.map(x => this.__(x.text, this.sidebarTranslationParameters)).join(' / ') : 'Error'
            this.title = translationKey + ' - Nitro'
        },
        closeToaster () {
            this.showErrorToast = false
        }
    }
}
</script>

<style lang="scss" scoped>
    .app {
        width: 100vw;
        height: 100vh;
        overflow: hidden;
        display: grid;
        grid-template-rows: auto 1fr;

        & .d-main-wrapper {
            display: grid;
            grid-template-rows: auto 1fr;
            overflow: hidden;

            & .d-main {
                overflow-y:scroll;
                height: 100%;
                padding-bottom: 80px;
            }
        }

        & .toaster {
            margin-bottom: 5rem !important;
            margin-right: 3rem !important;
            z-index: 20000 !important;
        }
    }
</style>
